import { useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import Spinner from "components/base/Spinner";
import { useModals } from "ModalProvider";
import { OdooContext } from "OdooProvider";
import { useContext, useState } from "react";
import { FaLink } from "react-icons/fa6";
import ConnectRetailerDeliveryAddressView from "./ConnectRetailerDeliveryAddressView";
import ConnectRetailerView from "./ConnectRetailerView";
import {
  FETCH_MT_ODOO_REPGROUPS,
  FETCH_MT_ODOO_RETAILERS,
  FETCH_ODOO_MT_ORDERS,
  FETCH_ODOO_PRODUCTS_WITH_MT_IDS,
} from "./graphql";
import SendToOdooView from "./SendToOdooView";

const OrdersView = ({ data, q }) => {
  const res = JSON.parse(data.mtOrders);
  const orders = res.response;

  const sourceIds = orders.map((i) => i.recordID);
  const markettimeRetailerIds = orders.map((i) => i.retailerID);
  const markettimeRepgroupIds = orders.map((i) => i.repGroupID);
  const mtProductIds = orders.flatMap((order) =>
    order.details.map((detail) => detail.itemID),
  );

  const { odooUrlInc } = useContext(OdooContext);
  const fetchMtOdooRepgroups = useQuery(FETCH_MT_ODOO_REPGROUPS, {
    variables: { markettimeRepgroupIds },
  });
  const fetchMtOdooCustomers = useQuery(FETCH_MT_ODOO_RETAILERS, {
    variables: { markettimeRetailerIds },
  });
  const fetchOdooProductsWithMtIds = useQuery(FETCH_ODOO_PRODUCTS_WITH_MT_IDS, {
    variables: { mtIds: mtProductIds },
  });
  const fetchMtOdooOrders = useQuery(FETCH_ODOO_MT_ORDERS, {
    variables: { sourceIds },
  });

  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const modal = useModals();

  const loading =
    fetchMtOdooRepgroups.loading ||
    fetchMtOdooCustomers.loading ||
    fetchOdooProductsWithMtIds.loading ||
    fetchMtOdooOrders.loading;
  const error =
    fetchMtOdooRepgroups.error ||
    fetchMtOdooCustomers.error ||
    fetchOdooProductsWithMtIds.error ||
    fetchMtOdooOrders.error;

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const odooCustomers = JSON.parse(
    fetchMtOdooCustomers.data.odooCustomers,
  ).records;
  const odooRepgroups = JSON.parse(
    fetchMtOdooRepgroups.data.odooRepgroups,
  ).records;
  const odooSaleOrders = JSON.parse(
    fetchMtOdooOrders.data.odooSaleOrders,
  ).records;
  const odooProducts = JSON.parse(fetchOdooProductsWithMtIds.data.odooProducts);

  console.log("orders", orders);
  console.log("odooCustomers", odooCustomers);
  console.log("odooRepgroups", odooRepgroups);
  console.log("odooSaleOrders", odooSaleOrders);

  const mappedOrders = orders
    .filter((i) =>
      q ? i.poNumber.toLowerCase().includes(q.toLowerCase()) : true,
    )
    .map((order) => {
      const odooSaleOrder = odooSaleOrders.find(
        (saleOrder) =>
          `${saleOrder.x_studio_source_id}` === `${order.recordID}`,
      );

      const retailerOdooCustomers = odooCustomers.filter(
        (customer) => customer.x_studio_mt_retailer_id === order.retailerID,
      );

      console.log("retailerOdooCustomers", retailerOdooCustomers);

      const odooCustomer =
        retailerOdooCustomers.length === 1 ? retailerOdooCustomers[0] : null;

      const odooRepgroup = odooRepgroups.find(
        (repgroup) => repgroup.x_studio_mt_repgroup_id === order.repGroupID,
      );

      let shipTo = order.shipToName;
      if (order.shipToAddress1) shipTo += `, ${order.shipToAddress1}`;
      if (order.shipToAddress2) shipTo += `, ${order.shipToAddress2}`;
      if (order.shipToCity) shipTo += `, ${order.shipToCity}`;
      if (order.shipToState) shipTo += `, ${order.shipToState}`;
      if (order.shipToZip) shipTo += `, ${order.shipToZip}`;
      if (order.shipToCountry) shipTo += `, ${order.shipToCountry}`;

      const odooDeliveryAddress = odooCustomer
        ? odooCustomer.child_ids.find(
            (address) =>
              address.type === "delivery" &&
              address.x_studio_mt_address_id === `${order.retailerShipToID}`,
          )
        : null;

      console.log("odooDeliveryAddress", odooDeliveryAddress);

      const orderLines = order.details.map((i) => ({
        ...i,
        odooProduct: odooProducts.find(
          (p) => p.x_studio_markettime_product_id === `${i.itemID}`,
        ),
      }));

      const hasProductsNotConnected = orderLines.some(
        (line) => !line.odooProduct,
      );

      const verified =
        odooCustomer &&
        odooRepgroup &&
        odooDeliveryAddress &&
        !hasProductsNotConnected &&
        !odooSaleOrder;

      return {
        ...order,
        shipTo,
        orderLines,
        odooSaleOrder,
        odooCustomer,
        retailerOdooCustomers,
        odooRepgroup,
        odooDeliveryAddress,
        hasProductsNotConnected,
        verified,
      };
    });

  const verifiedOrders = mappedOrders.filter((order) => order.verified);

  function sendToOdoo() {
    const selectedOrders = mappedOrders.filter((order) =>
      selectedOrderIds.includes(order.recordID),
    );
    modal.present({
      title: "Send Orders to Odoo",
      children: (
        <SendToOdooView
          orders={selectedOrders}
          hide={modal.hide}
          onCompleted={() => {
            modal.hide();
            setSelectedOrderIds([]);
          }}
        />
      ),
    });
  }

  function tryConnectRetailer(order) {
    modal.present({
      title: "Connect Retailer between Odoo and MarketTime",
      subtitle: order.billToName,
      children: (
        <ConnectRetailerView
          markettimeRetailerIds={markettimeRetailerIds}
          markettimeRepgroupIds={markettimeRepgroupIds}
          order={order}
          hide={modal.hide}
        />
      ),
    });
  }

  function tryConnectDeliveryAddress(order) {
    if (!order.odooCustomer) {
      window.alert("Please connect the retailer first");
      return;
    }

    const addressOptions = order.odooCustomer.child_ids.filter(
      (address) => address.type === "delivery",
    );

    modal.present({
      title: "Connect Delivery Address",
      subtitle: order.shipTo,
      children: (
        <ConnectRetailerDeliveryAddressView
          order={order}
          hide={modal.hide}
          options={addressOptions}
          markettimeRetailerIds={markettimeRetailerIds}
          markettimeRepgroupIds={markettimeRepgroupIds}
        />
      ),
    });
  }

  return (
    <div className="flex flex-col h-full overflow-auto">
      <div className="flex-1 overflow-auto">
        <table className="font-code ">
          <thead>
            <tr className="whitespace-nowrap sticky top-0 z-10 bg-gray-50 dark:bg-gray-700">
              <th className="w-4 py-3 px-4">
                <input
                  type="checkbox"
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedOrderIds(
                        verifiedOrders.map((order) => order.recordID),
                      );
                    } else {
                      setSelectedOrderIds([]);
                    }
                  }}
                  disabled={verifiedOrders.length === 0}
                  checked={
                    verifiedOrders.length > 0 &&
                    selectedOrderIds.length === verifiedOrders.length
                  }
                />
              </th>
              {selectedOrderIds.length > 0 ? (
                <th colSpan={8} className="px-4">
                  <div className="flex items-center space-x-4">
                    <div>{selectedOrderIds.length} Selected Orders</div>
                    <Button onClick={sendToOdoo}>Send to Odoo</Button>
                    <Button onClick={() => setSelectedOrderIds([])} danger>
                      Clear Selection
                    </Button>
                  </div>
                </th>
              ) : (
                <>
                  <th>ID</th>
                  <th>Order Date</th>
                  <th>Retailer</th>
                  <th>PO Number</th>
                  <th>Products</th>
                  <th className="text-right">Total</th>
                  <th className="text-right">M Status</th>
                  <th className="text-right px-4">Odoo</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {mappedOrders.map((order, index) => (
              <tr
                key={index}
                className="border-y border-gray-100 dark:border-gray-700"
              >
                <td className="px-4">
                  <input
                    type="checkbox"
                    disabled={!order.verified}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedOrderIds([
                          ...selectedOrderIds,
                          order.recordID,
                        ]);
                      } else {
                        setSelectedOrderIds(
                          selectedOrderIds.filter(
                            (id) => id !== order.recordID,
                          ),
                        );
                      }
                    }}
                    checked={selectedOrderIds.includes(order.recordID)}
                  />
                </td>
                <td>{order.recordID}</td>
                <td className="whitespace-nowrap">{order.orderDate}</td>
                <td className="font-sans">
                  <div className="space-y-1 whitespace-nowrap">
                    <div className="flex space-x-2">
                      <div>
                        <span className={!order.odooRepgroup && "text-red-500"}>
                          {order.repGroupID}
                        </span>{" "}
                        |{" "}
                        <b className={!order.odooCustomer && "text-red-500"}>
                          {order.billToName} (#{order.retailerID})
                        </b>
                      </div>
                      <Button onClick={() => tryConnectRetailer(order)}>
                        <FaLink />
                      </Button>

                      {order.odooCustomer && (
                        <a
                          className="text-purple-600"
                          href={`${odooUrlInc}/odoo/customers/${order.odooCustomer.id}`}
                          target="_blank"
                        >
                          View in Odoo
                        </a>
                      )}
                    </div>

                    {order.retailerOdooCustomers.length > 1 && (
                      <div className="text-red-600 text-xs whitespace-pre-wrap">
                        <p>
                          ⚠️ Multiple Odoo Customers found for the retailer(ID:
                          {order.retailerID}), Odoo Customers:{" "}
                          <span className="space-x-2">
                            {order.retailerOdooCustomers.map((c) => (
                              <a
                                className="text-purple-600"
                                href={`${odooUrlInc}/odoo/customers/${c.id}`}
                                target="_blank"
                              >
                                {c.name}
                              </a>
                            ))}
                          </span>
                          .
                        </p>
                        <p>
                          Please connect the only correct one and refresh this
                          page.
                        </p>
                      </div>
                    )}

                    <div>
                      <span
                        className={`
                            whitespace-normal align-middle
                            ${!order.odooDeliveryAddress && "text-red-500"}`}
                      >
                        Ship to: {order.shipTo} (#{order.retailerShipToID}){" "}
                        <span className=" align-middle">
                          <Button
                            inline
                            onClick={() => tryConnectDeliveryAddress(order)}
                          >
                            <FaLink />
                          </Button>
                        </span>
                      </span>
                    </div>
                  </div>
                </td>
                <td className="whitespace-nowrap">{order.poNumber}</td>
                <td className="whitespace-normal">
                  <div className="flex flex-wrap">
                    {order.orderLines.map((i, index) => (
                      <span
                        key={index}
                        className={`mr-1
                        ${!i.odooProduct && "text-red-500"}`}
                      >
                        {i.itemNumber}({i.quantity})
                      </span>
                    ))}
                  </div>
                </td>
                <td className="text-right">{order.orderTotal}</td>
                <td className="text-right">{order.manufacturerOrderStatus}</td>
                <td className="text-right whitespace-nowrap px-4">
                  {order.odooSaleOrder ? (
                    <a
                      className="text-purple-600"
                      href={`${odooUrlInc}/odoo/sales/${order.odooSaleOrder.id}`}
                      target="_blank"
                    >
                      {order.odooSaleOrder.name}
                    </a>
                  ) : (
                    "-"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-2 opacity-70 px-4 py-2 bg-gray-50 dark:bg-gray-700">
        🔴 Text in red means that the data is not connected correctly between
        Markettime and Odoo. Connect them before send to Odoo. If you have any
        questions, contact Stella/Charlie for assistance.
      </div>
    </div>
  );
};

export default OrdersView;
